import { formatCurrency, formatDate } from '@angular/common';
import { Component, computed, inject, input, numberAttribute, OnInit, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  ColDef,
  GetDetailRowDataParams,
  GridApi,
  GridOptions,
  GridSizeChangedEvent,
  RowSelectedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { DateTime } from 'luxon';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { finalize } from 'rxjs/operators';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../../error.utils';
import { defaultTableDefOptions } from '../../../helpers';
import { AssetRecoveryService } from '../../../service/asset-recovery.service';
import { NotifyService } from '../../../service/notify.service';
import {
  AssetRecoveryCase,
  AssetRecoveryIncident,
  AssetRecoveryStatus,
  AssetRecoverySummaryReport,
  Branch,
  LocationResponse,
} from '../../../types';
import { CellAssetRecoveryActionsComponent } from './cell-asset-recovery-actions/cell-asset-recovery-actions.component';
import { CellAssetRecoveryIdComponent } from './cell-asset-recovery-id/cell-asset-recovery-id.component';
import { CellAssetRecoverySummaryActionsComponent } from './cell-asset-recovery-summary-actions/cell-asset-recovery-summary-actions.component';

@Component({
  selector: 'app-asset-recovery',
  templateUrl: './asset-recovery.component.html',
  standalone: false,
})
export class AssetRecoveryComponent implements OnInit {
  private assetRecoveryService = inject(AssetRecoveryService);
  private notifyService = inject(NotifyService);
  private router = inject(Router);

  public branchId = input<string>('');
  public branchData = input<Branch>();
  public locationId = input<number, unknown>(undefined, { transform: numberAttribute });
  public locationData = input<LocationResponse>();

  public caseDateFrom = linkedQueryParam<Date>('case_date_from', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : DateTime.now().minus({ days: 30 }).toJSDate()),
    stringify: (value) => DateTime.fromJSDate(value).toFormat('yyyy-MM-dd'),
  });
  public caseDateTo = linkedQueryParam<Date>('case_date_to', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : DateTime.now().toJSDate()),
    stringify: (value) => DateTime.fromJSDate(value).toFormat('yyyy-MM-dd'),
  });
  public caseDateFromMin = computed(() => DateTime.fromJSDate(this.caseDateTo()).minus({ days: 180 }).toJSDate());
  public caseDateToMax = computed(() => DateTime.now().toJSDate());

  public loading = signal<boolean>(false);
  public creatingReport = signal<boolean>(false);
  public arSummaryLoading = signal<boolean>(false);
  public cases = signal<AssetRecoveryCase[]>([]);
  public selectedIncidentIds = signal<Set<string>>(new Set());
  public canCreateReport = computed(() => this.selectedIncidentIds().size > 0 && !this.loading() && !this.creatingReport());
  public selectedCaseId = computed(() => {
    const selectedIncidentIds = this.selectedIncidentIds();
    return this.cases().find((caseData) => caseData.incidents.some((incident) => selectedIncidentIds.has(incident.review_outcome_id)))
      ?.case_id;
  });

  public arSummaryModal = viewChild<PanModalComponent>('arSummaryModal');
  public arSummaryReports = signal<AssetRecoverySummaryReport[]>([]);
  private gridApi?: GridApi<AssetRecoveryCase>;

  public readonly columnDefs: ColDef<AssetRecoveryCase>[] = [
    {
      headerName: 'Case #',
      field: 'case_index',
      sortable: true,
      filter: 'agNumberColumnFilter',
      suppressHeaderMenuButton: false,
      cellRenderer: 'agGroupCellRenderer',
    },
    {
      headerName: 'Total Incidents',
      sortable: true,
      filter: 'agNumberColumnFilter',
      suppressHeaderMenuButton: false,
      valueGetter: (params) => params.data?.incidents.length ?? 0,
    },
    {
      headerName: 'Pending Incidents',
      colId: 'pending_incidents',
      sortable: true,
      filter: 'agNumberColumnFilter',
      suppressHeaderMenuButton: false,
      valueGetter: (params) => params.data?.incidents.filter((incident) => !incident.asset_recovery_id).length ?? 0,
    },
    {
      headerName: 'Total Amount At Risk',
      sortable: true,
      filter: 'agNumberColumnFilter',
      suppressHeaderMenuButton: false,
      valueGetter: (params) => params.data?.incidents.reduce((total, incident) => total + incident.amount, 0) ?? 0,
      valueFormatter: (params) => formatCurrency(params.value ?? 0, 'en-US', '$'),
    },
    {
      headerName: '',
      sortable: false,
      filter: false,
      suppressHeaderMenuButton: true,
      suppressKeyboardEvent: () => true,
      maxWidth: 100,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellAssetRecoverySummaryActionsComponent,
      cellRendererParams: {
        summaryCallback: (caseData: AssetRecoveryCase) => this.openArSummary(caseData),
      },
    },
  ];

  private readonly detailColumnDefs: ColDef<AssetRecoveryIncident>[] = [
    {
      headerName: 'Asset Recovery ID',
      field: 'asset_recovery_id',
      sortable: false,
      cellRenderer: CellAssetRecoveryIdComponent,
    },
    {
      headerName: 'Incident Date',
      field: 'incident_date',
      sortable: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return formatDate(params.value, 'MM/dd/yyyy', 'en-US');
      },
    },
    {
      headerName: 'Item Amount',
      field: 'amount',
      sortable: false,
      valueFormatter: (params) => formatCurrency(params.value ?? 0, 'en-US', '$'),
    },
    {
      headerName: 'Status',
      field: 'status',
      sortable: false,
      cellRenderer: (params) => this.renderStatus(params.value),
    },
    {
      headerName: '',
      sortable: false,
      suppressKeyboardEvent: () => true,
      maxWidth: 140,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellAssetRecoveryActionsComponent,
    },
  ];

  public readonly tableDef: GridOptions<AssetRecoveryCase> = {
    ...defaultTableDefOptions(),
    rowHeight: 56,
    masterDetail: true,
    detailRowAutoHeight: true,
    isRowMaster: (dataItem) => (dataItem?.incidents?.length ?? 0) > 0,
    detailCellRendererParams: () => ({
      detailGridOptions: {
        ...defaultTableDefOptions(),
        columnDefs: this.detailColumnDefs,
        domLayout: 'autoHeight',
        rowSelection: {
          mode: 'multiRow',
          checkboxLocation: 'selectionColumn',
          checkboxes: true,
          headerCheckbox: false,
          hideDisabledCheckboxes: false,
          isRowSelectable: (rowNode) => this.isIncidentSelectable(rowNode.data),
        },
        selectionColumnDef: {
          maxWidth: 55,
          suppressHeaderMenuButton: true,
        },
        getRowId: (params) => params.data.review_outcome_id,
        onRowSelected: (event: RowSelectedEvent<AssetRecoveryIncident>) => this.onIncidentRowSelected(event),
        onFirstDataRendered: (event) => {
          event.api.forEachNode((node) => {
            node.setSelected(this.selectedIncidentIds().has(node.data?.review_outcome_id ?? ''));
          });
        },
        defaultColDef: {
          resizable: false,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          sortable: false,
          cellClass: ['inline-flex', 'items-center'],
        },
        onGridSizeChanged: (params: GridSizeChangedEvent) => params.api.sizeColumnsToFit(),
      },
      getDetailRowData: (params: GetDetailRowDataParams<AssetRecoveryCase, AssetRecoveryIncident>) => {
        params.successCallback(params.data.incidents ?? []);
      },
    }),
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
    onGridReady: (params) => (this.gridApi = params.api),
    onGridSizeChanged: (params) => params.api.sizeColumnsToFit(),
  };

  ngOnInit() {
    this.load();
  }

  private renderStatus(status: AssetRecoveryStatus | null) {
    const statusLabel = status ?? 'Not Tracked';
    let statusColor = 'bg-default-no-hover';

    switch (status) {
      case AssetRecoveryStatus.Paid:
        statusColor = 'bg-success-no-hover';
        break;
      case AssetRecoveryStatus.Unpaid:
        statusColor = 'bg-danger-no-hover';
        break;
    }

    return `<span class="${statusColor} rounded text-sm px-2 py-1 font-medium">${statusLabel}</span>`;
  }

  openArSummary(caseData: AssetRecoveryCase) {
    const locationId = this.locationId();
    if (!locationId) return;

    this.arSummaryReports.set([]);
    this.arSummaryLoading.set(true);
    this.arSummaryModal()?.show();
    this.assetRecoveryService
      .getAssetRecoverySummaryForCase(locationId, caseData.case_id)
      .pipe(finalize(() => this.arSummaryLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.arSummaryReports.set(response.items ?? []);
        },
        error: (error) => {
          this.notifyService.notify('Failed to load asset recovery summary: ' + tryGetErrorMessage(error));
        },
      });
  }

  private isIncidentSelectable(incident?: AssetRecoveryIncident) {
    if (!incident || incident.asset_recovery_id) return false;

    const selectedCaseId = this.selectedCaseId();
    if (!selectedCaseId) return true;
    return this.cases().some(
      (caseData) =>
        caseData.case_id === selectedCaseId && caseData.incidents.some((i) => i.review_outcome_id === incident.review_outcome_id),
    );
  }

  private onIncidentRowSelected(event: RowSelectedEvent<AssetRecoveryIncident>) {
    const incidentId = event.data?.review_outcome_id;
    if (!incidentId) return;

    this.selectedIncidentIds.update((selectedIncidentIds) => {
      const updatedIds = new Set(selectedIncidentIds);
      if (event.node.isSelected()) updatedIds.add(incidentId);
      else updatedIds.delete(incidentId);

      return updatedIds;
    });
    this.gridApi?.forEachDetailGridInfo((detailGrid) => {
      detailGrid.api.setGridOption('rowSelection', {
        mode: 'multiRow',
        checkboxLocation: 'selectionColumn',
        checkboxes: true,
        headerCheckbox: false,
        hideDisabledCheckboxes: false,
        isRowSelectable: (rowNode) => this.isIncidentSelectable(rowNode.data),
      });
    });
  }

  reportNameValidate(name: string) {
    const reportName = name.trim();
    if (reportName.length < 4) return 'Report name too short. Min 4 characters.';
    if (reportName.length > 25) return 'Report name too long. Max 25 characters.';

    return null;
  }

  createReport(reportName: string) {
    const title = reportName.trim();
    if (!title) return;

    const locationId = this.locationId();
    if (!locationId) return;

    this.creatingReport.set(true);
    this.assetRecoveryService
      .createAssetRecoveryReport(locationId, {
        review_outcome_ids: [...this.selectedIncidentIds()],
        title,
      })
      .pipe(finalize(() => this.creatingReport.set(false)))
      .subscribe({
        next: (report) => {
          this.notifyService.notify('Asset Recovery report created successfully');
          this.selectedIncidentIds.set(new Set());
          this.router.navigate(['/shared', 'asset-recovery-report', report.id]);
        },
        error: (error) => {
          this.notifyService.notify('Failed to create asset recovery report: ' + tryGetErrorMessage(error));
        },
      });
  }

  load() {
    const locationId = this.locationId();
    if (!locationId) return;

    this.loading.set(true);
    this.assetRecoveryService
      .getAssetRecoveryCasesForLocation(locationId, this.caseDateFrom(), this.caseDateTo())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          this.selectedIncidentIds.set(new Set());
          this.cases.set(response.items ?? []);
        },
        error: (error) => {
          this.notifyService.notify('Failed to load asset recovery cases: ' + tryGetErrorMessage(error));
        },
      });
  }
}
